import { MouseEvent, useState } from 'react';

import ListIcon from '@mui/icons-material/List';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import GlobeHemisphereWest from '@src/constants/img/GlobeHemisphereWest.png';
import Logo from '@src/constants/img/logo2.png';
import { LANGUAGES } from '@src/constants/screen/dropdownList';
import ReactCountryFlag from 'react-country-flag';

import { AccountPopover } from '@src/components/Account';
import { useTranslation } from 'react-i18next';
import { Fab } from '@mui/material';
import { useNavigate } from 'react-router';
import { useGlobalState } from '@src/context/GlobalStateContext';
import { EmissionsSource, IMiceForm } from '@src/types';

const DashboardNav = () => {
    const { i18n, t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate()
    const { setFormMice, setGasEmissions } = useGlobalState();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [mobileNavValue, setMobileNavValue] = useState(0);
    const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const onChangeLang = (e: MouseEvent<HTMLElement>) => {
        const { id } = e.currentTarget;
        i18n.changeLanguage(id);
        handleCloseUserMenu();
    };

    const handleBackHome = () => {
        window.location.href = '/';
    };

    const handleMobileNavChange = (_event: React.SyntheticEvent, newValue: number) => {
        switch (newValue) {
            case 1:
                sessionStorage.setItem('tmpCreateData', '');
                localStorage.removeItem('formMice');
                localStorage.removeItem('gasEmissions');
                sessionStorage.removeItem('tmpEventId');
                setFormMice({} as IMiceForm);
                setGasEmissions({} as EmissionsSource);
                navigate('register/pre/1');
                break;
            case 2:
                navigate('/setting');
                break;
            default:
                navigate('/')
        }

        console.log('newValue', newValue)
        setMobileNavValue(newValue);
    };

    return (
        <>
            <AppBar position="static" color="default" sx={{
                // Mobile overflow handling
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100vw',

                // Responsive padding
                px: { xs: 1, sm: 2, md: 3 },

                // Prevent horizontal scroll
                '& .MuiToolbar-root': {
                    overflow: 'hidden',
                    minHeight: { xs: 56, sm: 64 },
                    px: { xs: 0.5, sm: 2 },

                    '& > *': {
                        flexShrink: 1,
                        minWidth: 0, // Important for text truncation
                    }
                }
            }}>
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {/* Mobile Layout */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' }, width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <img src={GlobeHemisphereWest} alt="Language" width="24" />
                            </IconButton>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                                <img src={Logo} alt="Logo" width="80" className="cursor-pointer" onClick={handleBackHome} />
                            </Box>

                            <AccountPopover data={[]} />
                        </Box>

                        {/* Desktop Layout */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, width: '100%', alignItems: 'center' }}>
                            <img src={Logo} alt="Logo" width="100" className="cursor-pointer" onClick={handleBackHome} />
                            <Typography
                                variant="h6"
                                noWrap
                                component="a"
                                href="/"
                                sx={{
                                    mr: 2,
                                    ml: 6,
                                    fontWeight: 700,
                                    fontFamily: 'Kanit, sans-serif',
                                    fontSize: 'x-large',
                                    color: '#536dfe',
                                    textDecoration: 'none',
                                }}
                            >
                                Zero Carbon MICE
                            </Typography>

                            <Box sx={{ flexGrow: 1 }} />

                            <Box sx={{ display: 'flex', gap: '10px' }}>
                                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                    <img src={GlobeHemisphereWest} alt="Language" />
                                </IconButton>
                                <AccountPopover data={[]} />
                            </Box>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Bottom Navigation */}
            {isMobile && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }}>
                    <BottomNavigation
                        showLabels
                        value={mobileNavValue}
                        onChange={handleMobileNavChange}
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                        }}
                    >
                        <BottomNavigationAction
                            label={t('LIST')}
                            icon={<ListIcon />}
                            sx={{
                                '&.Mui-selected': {
                                    color: '#536dfe',
                                },
                            }}
                        />
                        <BottomNavigationAction
                            label={t('BUTTONS.CREATE_PROJECT')}
                            icon={
                                <Fab
                                    size="large"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 22,
                                        backgroundColor: mobileNavValue === 1 ? '#e0e0e0' : '#f5f5f5',
                                        color: mobileNavValue === 1 ? 'black' : 'grey.700',
                                        boxShadow: 'none',
                                        border: '1px solid #e0e0e0',
                                        '&:hover': {
                                            backgroundColor: '#d5d5d5',
                                        },
                                    }}
                                >
                                    <CalculateIcon />
                                </Fab>
                            }
                            sx={{
                                '&.Mui-selected': {
                                    color: 'primary.main',
                                },
                                '& .MuiBottomNavigationAction-label': {
                                    position: 'relative',
                                    top: '14px',
                                },
                            }}
                        />
                        <BottomNavigationAction
                            label={t('SETTING_PAGE.HEADER')}
                            icon={<SettingsIcon />}
                            sx={{
                                '&.Mui-selected': {
                                    color: '#536dfe',
                                },
                            }}
                        />
                    </BottomNavigation>
                </Box>
            )}

            {/* Language Menu */}
            <Menu
                sx={{
                    mt: '45px',
                    '& .MuiPaper-root': {
                        minWidth: { xs: 180, sm: 200 },
                        maxWidth: { xs: 250, sm: 300 },
                    }
                }}
                id="menu-language"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                {LANGUAGES?.map(({ code, label, flag }) => (
                    <MenuItem
                        key={code}
                        value={code}
                        id={code}
                        onClick={onChangeLang}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: { xs: 1, sm: 1.5 },
                            px: { xs: 1.5, sm: 2 },
                            minHeight: { xs: 40, sm: 48 },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flexGrow: 1,
                                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                            }}
                            noWrap
                        >
                            {label}
                        </Typography>
                        <ReactCountryFlag
                            countryCode={flag}
                            svg
                            style={{
                                width: '1.2em',
                                height: '1.2em',
                                marginLeft: 8,
                                flexShrink: 0
                            }}
                            title={flag}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default DashboardNav;
